import { MediaPlayerProxy } from './media-player-proxy';
import { AMP } from './globals';
import { OmnitureTracking } from './tracking/omniture/omniture-tracking';
import MediaAnalyticsTracking from './tracking/media-analytics/media-analytics-tracking';

// import { TapForSoundPlugin } from './amp-plugins/tap-for-sound/plugin';
import { AmpEventsSourcesPlugin } from './amp-plugins/amp-events-sources/plugin';
import { debug } from './utils/debug-log';
import '@medscape/streaming-video-player/src/sass/medscape-audio-player.scss';
import { EVENT_READY as AMP_EVENTS_SOURCES_READY } from './amp-plugins/amp-events-sources/amp-events-sources.constants';
import { getMediaConfig } from "./tracking/media-analytics/media-config";
const { Promise, MutationObserver } = window;
if (AMP.plugins.AmpEventsSources == null) {
    AMP.registerPlugin("AmpEventsSources", AmpEventsSourcesPlugin.create());
}


export class MedscapeAudioPlayer extends MediaPlayerProxy {
    constructor(containerEl, playerSettings, trackingSettings) {
        super(containerEl, Object.assign({}, playerSettings, { "medium": "audio" }), trackingSettings);
        if(containerEl.querySelector('audio') == null) {
            containerEl.appendChild(document.createElement('audio'));
        }
        debug.log('[MedscapeAudioPlayer] Container:\n', containerEl, "\nPlayer Settings:\n", playerSettings, "\nTracking Settings:\n", trackingSettings);
    }
    async create() {
        let { containerEl, el, ampConfig } = this;

        containerEl.appendChild(el);
        
        debug.log("[MedscapeAudioPlayer] AmpConfig:", ampConfig);
        const mediaConfig = await getMediaConfig();
        return new Promise((resolve, reject) => {
            AMP.create(el, ampConfig).then((amp) => {
                debug.log("[MedscapeAudioPlayer] AMP.create:", amp);
 
                this.amp = amp;

                containerEl.appendChild(el);

                // AKAMAI initially creates a 'video' insead of an 'audio' HTML Element 
                // and replaces it later in it's lifecycle
                // Akamai uses 'VIDEO' HTML Ememnt for HLS audio
                if (amp.mediaElement.tagName !== "AUDIO" && amp.getConfig().media.source[0].type !== 'application/x-mpegURL') {
                    let targetNode = amp.container;

                    const OBSERVER_CONFIG = { attributes: false, childList: true, subtree: true };
                    const callback = (mutationsList, observer) => {
                        
                        for (const mutation of mutationsList) {
    
                            if (mutation.type === 'childList') {
                                if (amp.mediaElement.tagName === "AUDIO") {
                                    observer.disconnect();
                                    if (amp.AmpEventsSources == null) {
                                        amp.addEventListener(AMP_EVENTS_SOURCES_READY, e => {
                                            this.init();
                                            resolve(amp);
                                        })
                                    } else {
                                        this.init();

                                        resolve(amp);
                                    }
                                }
                            }
                        }
                    };
                    const observer = new MutationObserver(callback);

                    observer.observe(targetNode, OBSERVER_CONFIG);

                } else if (amp.AmpEventsSources == null) {
                    amp.addEventListener(AMP_EVENTS_SOURCES_READY, e => {
                        this.init();

                        resolve(amp);
                    })
                } else {
                    this.init();
                    resolve(amp);
                }

            }, error => {
                debug.error("Error Creating AMP:", error);
                reject(error)
            });
        });

    }
    destroy() {
        this.handleAmpDestroy()
        this.amp.destroy();
    }
    destroySubscripttions() {
        this.subscriptions.forEach(subscription => {
            if (subscription != null && subscription.unsubscribe != null) {
                subscription.unsubscribe();
            }
        });
        this.subscriptions = [];
    }
    destroyOmnitureTracking() {
        if (this.omnitureTracking != null) {
            debug.log("[MedscapeAudioPlayer] Destroy Omniture Tracking");
            this.omnitureTracking.destroy();
            this.omnitureTracking = null;
        }
    }
    destroyMediaAnalyticsTracking() {
        if (this.mediaAnalyticsTracking != null) {
            this.mediaAnalyticsTracking.destroy();
            this.mediaAnalyticsTracking = null;
        }
    }
    handleAmpDestroy() {
        this.destroySubscripttions();
        this.destroyOmnitureTracking();
        this.destroyMediaAnalyticsTracking();
    }
    handleCaptionChange(eventData) {
        //debug.log("[MedscapeAudioPlayer] handleCaptionChange:", eventData);
        // Prevent blank black caption box from whitespaces
        let { event: e, amp } = eventData;
        if (e.data && e.data.length > 0) {
            let { text } = e.data[0];
            const hasText = text.trim().length > 0;
            if (!hasText) {
                amp.container.classList.add("svp-cc-empty-text");
            } else {
                amp.container.classList.remove("svp-cc-empty-text");
            }
        }
    }
    initTogglePlayPause() {
        let { amp } = this;
        debug.log("[MedscapeAudioPlayer] Add Toggle Play/Pause Click Event Listener");
        amp.mediaElement.addEventListener('click', e => {
            debug.log("MedscapeAudioPlayer] amp.mediaElement Clicked, Event:", e);
            const { buffering } = amp;
            const { paused } = amp.mediaElement
            if (buffering === true) {
                return;
            } else if (paused === false) {
                amp.pause();
            } else {
                amp.play();
            }
        });
    }
    init() {
        this.destroySubscripttions();
        debug.log("[MedscapeAudioPlayer] init");
        let { amp } = this;
        // Akamai first creates a video element and switches over to an audio element
        // after the player is 'ready'
        this.initTogglePlayPause();
        debug.log("[MedscapeAudioPlayer] init - amp.controls:", amp.controls);
        this.initSubscriptions();
        this.initTracking();

        this.el.svp = this;
    }
    initSubscriptions() {
        let { amp, ampConfig } = this;
        // FIX: remove native controls enabled
        if (amp.controls === true) {
            debug.log("[MedscapeAudioPlayer] initSubscriptions - Hide Native Controls:");
            amp.mediaElement.controls = false;
            // FIX fullscreen with display native controls with the react controls
            let fullscreen$ = amp.AmpEventsSources["fullscreenchange"].subscribe(eventData => {
                eventData.amp.mediaElement.controls = false;
            });
            this.subscriptions.push(fullscreen$);
            let fixControls$ = amp.AmpEventsSources["timeupdate"].subscribe(eventData => {
                //debug.log("[MedscapeAudioPlayer] event:loadeddata:",eventData);
                eventData.amp.mediaElement.controls = false;
                fixControls$.unsubscribe();
            });
            this.subscriptions.push(fixControls$);
        }


        let autoplayblocked$ = amp.AmpEventsSources["autoplayblocked"].subscribe(eventData => {
            debug.warn("[MedscapeAudioPlayer] EVENT - Autoplay Blocked:", eventData);
            debug.log("[MedscapeAudioPlayer] AMP muted:", eventData.amp.muted);
            debug.log("[MedscapeAudioPlayer] Media Element muted:", eventData.amp.mediaElement.muted);

            // Autoplay won't work until user interacts with the page    
            let { amp } = eventData;
            amp.setWaiting(false);
            amp.container.querySelector('.amp-ui').classList.remove('amp-waiting');

        })
        // Fix Start Time Defect in AMP
        let loadeddata$ = amp.AmpEventsSources["loadeddata"].subscribe(eventData => {
            debug.log("[MedscapeAudioPlayer] EVENT - %cLoaded Data:", "color: yellow;", "\n", eventData);
            const { amp, state } = eventData;
            const { startTime } = amp.media.metadata;
            if (startTime && startTime > 0) {
                debug.warn("[MedscapeAudioPlayer] AMP BUGFIX: Fix Start Time Defect", startTime);
                amp.setCurrentTime(startTime);
            }
            if (state.isMuted != ampConfig.muted) {
                debug.warn("[MedscapeAudioPlayer] AMP BUGFIX: Fix Mute/Unmute state on setMedia");
                if (this.ampConfig.muted) {
                    amp.mute();
                } else {
                    amp.unmute();
                }
            }
            // Fix AMP hides audio player's controls
            debug.log("[MedscapeAudioPlayer] Loaded Data Controls:", amp.mediaElement.controls);
            //amp.mediaElement.controls = true;
        })

        let mediachange$ = amp.AmpEventsSources.mediachange.subscribe(eventData => {
            debug.log("[MedscapeAudioPlayer] %cMedia Changed:", "color: yellow;", "\n", eventData);
            this.initTracking();

        });

        let mutechange$ = amp.AmpEventsSources["mutechange"].subscribe(eventData => {
            this.lastEventState = eventData.state;
            if (eventData.state.isMuted == false) {
                // Flag that autoplay videos don't need to be muted anymore
                this.isUnmuted = true;
            }

        })

        let captionCueChange$ = amp.AmpEventsSources["captioningcuechange"].subscribe(eventData => {
            this.handleCaptionChange(eventData);
        });

        let destroy$ = amp.AmpEventsSources["destroy"].subscribe(eventData => {
            debug.log("[MedscapeAudioPlayer] Destroy!", eventData);
            this.lastEventState = eventData.state;
            this.handleAmpDestroy();
        })
        debug.log("[MedscapeAudioPlayer] Test Controls", amp.mediaElement.controls);

        this.subscriptions.push(loadeddata$);
        this.subscriptions.push(mutechange$);
        this.subscriptions.push(mediachange$);
        this.subscriptions.push(destroy$);
        this.subscriptions.push(autoplayblocked$);
        this.subscriptions.push(captionCueChange$);
    }
    initOmnitureTracking() {
        this.destroyOmnitureTracking();
        debug.log("[MedscapeAudioPlayer] Init Omniture Tracking");
        let { amp, trackingSettings, playerSettings } = this;
        let omnitureTracking = new OmnitureTracking(trackingSettings, amp, playerSettings);
        omnitureTracking.init();
        this.omnitureTracking = omnitureTracking;
    }
    initMediaAnalyticsTracking() {
        this.destroyMediaAnalyticsTracking();
        let { amp, trackingSettings } = this;
        this.mediaAnalyticsTracking = new MediaAnalyticsTracking(amp, trackingSettings);
        this.mediaAnalyticsTracking.init();
    }
    initTracking() {
        debug.log("[MedscapeAudioPlayer] Init Tracking");
        this.initOmnitureTracking();
        this.initMediaAnalyticsTracking();
    }
    setMedia(playerSettings) {
        debug.log("[MedscapeAudioPlayer] setMedia: playerSettings", playerSettings);
        this.playerSettings = playerSettings;
        let { amp, ampConfig } = this;
        debug.log("[MedscapeAudioPlayer] setMedia: ampConfig.media", ampConfig.media);
        amp.setMedia(ampConfig.media);
    }
}